import React, {ReactElement} from "react";

import Image, {ImageObject} from "../../image/Image";
import CTAButton, {CTA} from "../../buttons/cta-button/CTAButton";
import LiteLeadForm from "../../lead-forms/lite-lead-form/LiteLeadForm";
import ModuleBody from "../../layouts/module-body/ModuleBody";
import SimpleFormWrapper from "../../forms/simple-form-wrapper/SimpleFormWrapper";
import MultiForm from "../../forms/multi-form/MultiForm";
import Video, {VideoType} from "../../videos/video/Video";
import Quote, {QuoteProps} from "../../cards/quote/Quote";

import {css, generateKeyId} from "/src/utils/utils";
import {MaybeType, MultiFormProps, SimpleFormProps} from "/src/types";

import * as styles from "./page-hero.module.scss";

interface PageHeroProps {
  alignLayout?: MaybeType<string>;
  eyebrow: MaybeType<string>;
  eyebrowIcon: MaybeType<ImageObject>;
  headline: MaybeType<string>;
  copy: MaybeType<string>;
  ctas: MaybeType<Array<CTA>>;
  bgImage: MaybeType<ImageObject>;
  bgVideo: MaybeType<string>;
  wistiaBgVideo?: string;
  darkOverlay: MaybeType<boolean>;
  reverseLayout?: MaybeType<boolean>; // replaced by alignLayout
  showLeadForm?: MaybeType<boolean>;
  isHomePage?: MaybeType<boolean>;
  pardotFormHandlerUrl: MaybeType<string>;
  leadFormHeadline: MaybeType<string>;
  leadFormButtonText: MaybeType<string>;
  leadFormCampaignId: MaybeType<string>;
  leadFormConfirmationRoute: MaybeType<string>;
  leadFormLastInquiryObject: MaybeType<string>;
  onLeadFormSubmitError?: (err: Error) => unknown;
  titleWithLineBreaks: MaybeType<string>;
  useTitleWithLineBreaks: MaybeType<boolean>;
  form?: MultiFormProps | SimpleFormProps;
  quote?: QuoteProps;
  theme?: "light" | "dark" | "grey";
}

const PageHero = (props: PageHeroProps): ReactElement => {
  const {
    alignLayout = "left",
    eyebrow,
    eyebrowIcon,
    headline,
    copy,
    bgImage,
    bgVideo,
    wistiaBgVideo,
    ctas,
    reverseLayout = false,
    showLeadForm = false,
    leadFormHeadline = "",
    leadFormButtonText = "",
    leadFormCampaignId,
    leadFormLastInquiryObject,
    leadFormConfirmationRoute,
    pardotFormHandlerUrl,
    onLeadFormSubmitError,
    darkOverlay,
    titleWithLineBreaks,
    useTitleWithLineBreaks,
    form,
    isHomePage,
    quote,
    theme,
  } = props;

  let leadForm = showLeadForm ? (
    <LiteLeadForm
      headline={leadFormHeadline!}
      buttonText={leadFormButtonText!}
      onSubmitError={onLeadFormSubmitError}
      campaignId={leadFormCampaignId!}
      lastInquiryObject={leadFormLastInquiryObject!}
      pardotFormHandlerUrl={pardotFormHandlerUrl}
      confirmationRoute={leadFormConfirmationRoute}
    />
  ) : null;

  // if there is form, and type === simple form + layout full || type multiform
  // we will use latest form component to render lead form
  /* eslint-disable no-underscore-dangle */
  if (form != null) {
    if (form.__typename === "ContentfulSimpleForm" && form.layout === "full") {
      leadForm = (
        <SimpleFormWrapper
          contentClasses={styles.pageHero__form__form__content}
          classes={styles.pageHero__form__form}
          {...form}
        />
      );
    } else if (form.__typename === "ContentfulMultiStepForm") {
      leadForm = <MultiForm {...form} />;
    }
  }

  const constructVideoComponent = () => {
    if (bgVideo == null && wistiaBgVideo == null) {
      return null;
    }

    if (wistiaBgVideo != null) {
      return (
        <div className={css(styles.pageHero__videoBg)}>
          <Video
            videoConfig={{
              wistia: {
                options: {
                  fitStrategy: "cover",
                },
              },
            }}
            videoTitle={headline!}
            type={VideoType.autoplay}
            videoSource={wistiaBgVideo}
            showControl={false}
          />
        </div>
      );
    }

    return (
      <div className={css(styles.pageHero__videoBg)}>
        <Video
          videoTitle={headline!}
          type={VideoType.autoplay}
          videoSource={bgVideo!}
          videoClasses={styles.pageHero__videoBg__video}
          showControl={false}
        />
      </div>
    );
  };

  return (
    <div
      className={css(
        styles.pageHero,
        theme === "grey" ? styles.pageHeroGrey : "",
        isHomePage ? styles.pageHeroHomePage : "",
        alignLayout === "right" || reverseLayout
          ? styles.pageHeroReversed
          : alignLayout === "center"
          ? styles.pageHeroCentered
          : "",
        leadForm != null ? styles.pageHeroHalf : ""
      )}
    >
      <div className={styles.pageHero__bgWrapper}>
        {!bgVideo && !wistiaBgVideo && bgImage && (
          <Image
            image={bgImage}
            objectFit="cover"
            classes={styles.pageHero__bgImg}
          />
        )}

        {constructVideoComponent()}

        {darkOverlay === true && (
          <div className={styles.pageHero__overlay}></div>
        )}
      </div>
      <ModuleBody classes={styles.pageHero__body}>
        <div className={styles.pageHero__content}>
          <div className={styles.pageHero__content__body}>
            {eyebrow && (
              <div className={styles.pageHero__content__eyebrowWrapper}>
                {eyebrowIcon && (
                  <Image
                    image={eyebrowIcon}
                    classes={styles.pageHero__content__eyebrowIcon}
                  />
                )}
                <p
                  className={css("eyebrow", styles.pageHero__content__eyebrow)}
                >
                  {eyebrow}
                </p>
              </div>
            )}

            {useTitleWithLineBreaks && titleWithLineBreaks != null && (
              <h1 className={css("h1", styles.pageHero__content__headline)}>
                <span
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html:
                      titleWithLineBreaks != null
                        ? titleWithLineBreaks.replace(/(\n|\r)/g, "<br/>")
                        : "",
                  }}
                />
              </h1>
            )}

            {!useTitleWithLineBreaks && headline != null && (
              <h1 className={css("h1", styles.pageHero__content__headline)}>
                {headline}
              </h1>
            )}

            {copy != null && (
              <p className={css("p", styles.pageHero__content__copy)}>{copy}</p>
            )}

            {/* we will also assume that the first CTA would be primary, subsequent cta should be secondary */}
            {ctas && (
              <ul className={styles.pageHero__content__ctas}>
                {ctas.map((cta, i) => {
                  return (
                    <li
                      key={generateKeyId(`${cta.label.toLowerCase()}-${i}`)}
                      className={styles.pageHero__content__cta}
                    >
                      <CTAButton
                        darkMode
                        cta={cta}
                        kind={i === 0 ? "primary" : "secondary"}
                      />
                    </li>
                  );
                })}
              </ul>
            )}

            {form != null &&
              form.__typename === "ContentfulSimpleForm" &&
              form.layout === "compact" && (
                <SimpleFormWrapper
                  {...form}
                  transparentBg={bgVideo != null}
                  darkMode
                  contentClasses={styles.pageHero__content__compactForm}
                />
              )}
          </div>

          {quote != null && (
            <Quote
              transparent={true}
              image={quote.image as ImageObject}
              name={quote.name!}
              quote={quote.quote!}
              role={quote.role!}
              contentClasses={styles.pageHero__quote}
            />
          )}
        </div>

        {/* Show image stacked on mobile view, instead of video, if for home page */}
        {!!isHomePage === true && bgVideo != null && bgImage != null && (
          <Image
            image={bgImage}
            objectFit="cover"
            classes={css(styles.pageHero__bgImg, styles.pageHero__bgImgMobile)}
          />
        )}

        {leadForm != null && (
          <div className={styles.pageHero__form}>{leadForm}</div>
        )}
      </ModuleBody>
    </div>
  );
};

export default PageHero;
